import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { zodSuccessResponseSchema } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { queryKeysCompany } from './queryKeys';

const zodCreateCompanyApiKeyEndpointSchema = z.tuple([
  z.literal('vadmin'),
  z.literal('company'),
  z.uuid(),
  z.literal('api_key'),
]);

/**
 * `ApiKey` in the OpenAPI spec — the `Success` envelope plus the newly
 * created key itself, which the previous type was missing entirely.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/create_api_key_vadmin_company__company_id__api_key_post
 */
const zodCreateCompanyApiKeyResponseSchema = zodSuccessResponseSchema.extend({
  apiKey: z.string(),
});

type CreateCompanyApiKeyResponse = z.infer<
  typeof zodCreateCompanyApiKeyResponseSchema
>;

/**
 * Creates a new API key for a company.
 *
 * @param companyId - The ID of the company to create an API key for.
 * @returns - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/create_api_key_vadmin_company__company_id__api_key_post
 */
// fallow-ignore-next-line unused-export
export const createCompanyApiKey = async (companyId: UUID) => {
  try {
    return await vestigasApi
      .post(ENDPOINT.COMPANY.API_KEY.CREATE(companyId), {
        validate: {
          isResponseSensitive: true, // The response carries the newly created secret; never log it.
          pathSegments: zodCreateCompanyApiKeyEndpointSchema,
          response: zodCreateCompanyApiKeyResponseSchema,
        },
      })
      .json<CreateCompanyApiKeyResponse>();
  } catch (error) {
    log.error({
      message: 'Error creating company API key',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * Custom hook for creating a company API key using mutations.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/create_api_key_vadmin_company__company_id__api_key_post
 */
export const useMutationCompanyCreateApiKey = (options?: {
  onSuccess?: (data: CreateCompanyApiKeyResponse, companyId: UUID) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation<CreateCompanyApiKeyResponse, Error, UUID>({
    mutationFn: createCompanyApiKey,
    onSuccess(_data, companyId) {
      queryClient.invalidateQueries({
        queryKey: queryKeysCompany.getApiKeys(companyId),
      });
    },
    ...options,
  });
};
